<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.permits.action_dates.action_date_dialog.title_edit') }}
    </h1>
    <div v-loading="!isDataLoaded">
      <el-form
        v-if="isDataLoaded"
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="submitForm"
      >
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.name')"
            :error="errors?.name?.[0]"
            :class="form.name ? 'has-value' : ''"
            prop="name"
          >
            <el-input
              v-model="form.name"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :error="errors?.description?.[0]"
            :class="form.description ? 'has-value' : ''"
            prop="description"
            class="tw-flex-3"
          >
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="5"
              :placeholder="$t('common.fields.description')"
              :maxlength="512"
            />
          </i-form-item>
        </div>
      </el-form>
    </div>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.edit') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { deepDiff, getValidationError } from '@/helpers';

const FORM_BASE = {
  name: '', // required, maxLength: 128
  description: '', // not required, but if filled - maxLength: 512
};

export default {
  name: 'EditPermitActionDateDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    documentId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      isDataLoaded: false,
      actionDate: {},
      errors: {},
      form: _cloneDeep(FORM_BASE),
      initialForm: {},
      formRules: {
        name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        description: [
          { max: 512, message: 'Max 512 characters', trigger: 'blur' },
        ],
      },
    };
  },

  watch: {
    actionDate() {
      this.setInitialFormData();
    },
    isOpen(val) {
      if (val) {
        this.loadPlaceDetails();
      }
    },
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.$refs.Form?.clearValidate();
    },

    async loadPlaceDetails() {
      this.isDataLoaded = false;
      this.actionDate = await this.$store.dispatch('v2/admin/cms/permitManagement/actionDates/getActionDateDetails', {
        id: this.documentId,
      });
      this.isDataLoaded = true;
    },

    setInitialFormData() {
      const documentKeys = Object.keys(this.actionDate);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (documentKeys.includes(key)) {
          this.form[key] = this.actionDate[key];
          this.initialForm[key] = this.actionDate[key];
        }
      });

      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/permitManagement/actionDates/editActionDate', {
          id: this.actionDate.id,
          actionDate: deepDiff(this.initialForm, this.form),
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
